import React, { useContext, useEffect, useState } from 'react';

import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { Link, navigate } from '@reach/router';

import { GlobalContext } from '../../../../../store';
import { IAdminClub } from '../../../../../store/admin/clubs/store';

import {
  redWaglLogo,
  addNewClubIcon,
  clubHouseLogo,
  settingsLogo,
  eventsLogo,
  clubHouseIcon,
  financesLogo,
  rosterLogo
  // leaderboardLogo
} from '../../../../../assets/images/';
import AutoComplete from '../../../../AutoComplete';
import SendNotification from '../SendNotification/SendNotification';
import useLocation from '../../../../../contrib/Hooks/useLocation.hook';

interface IProps {
  clubId: number;
  onClose: () => void;
  open: boolean;
}

export const AdminSideMenu = (props: IProps) => {
  const { clubId, onClose, open } = props;

  const {
    location: { pathname: currentRoute }
  } = useLocation();

  const {
    actions: {
      auth: { logout: logoutAction },
      seasons: { setCurrent: setCurrentSeason }
    },
    adminClubs,
    seasons
  } = useContext(GlobalContext);

  const [adminClub, setAdminClub] = useState<IAdminClub>();

  useEffect(() => {
    if (adminClubs && clubId) {
      setAdminClub(adminClubs.all[clubId]);
    }
  }, [clubId, adminClubs]);

  const isARouteWithPickers = (route: string): boolean => {
    const routesWithPickers = [/\/staff\/club\/[0-9]+\.*/, /\/staff\/home/, /\/staff\/profile/];

    return !!routesWithPickers.some(routePattern => routePattern.test(route));
  };

  const clubSeasons = clubId && seasons[clubId] ? seasons[clubId].seasons : [];
  const currentSeason = clubId && seasons[clubId] ? seasons[clubId].currentSeason : false;

  const switchToClub = (clubOption: any) => {
    onClose();

    navigate(`/staff/club/${clubOption.value}/`);
  };

  const switchToSeason = (seasonOption: any) => {
    const seasonToChangeTo = clubSeasons.find(season => season.year === seasonOption.value);

    if (seasonToChangeTo) {
      setCurrentSeason(clubId, seasonToChangeTo);
    }
    onClose();
  };

  const clubOptions = Object.values(adminClubs.all)
    .map(club => ({ label: club.name, value: club.id.toString() }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const selectedClub = clubOptions.find(club => club.value === String(clubId));

  const seasonOptions = (clubSeasons || [])
    .map(season => ({
      label: season.year,
      value: season.year
    }))
    .sort((a, b) => (a.value < b.value ? 1 : -1));

  const selectedSeason = currentSeason
    ? { label: currentSeason.year, value: currentSeason.year }
    : seasonOptions.reduce(
        (prev, current) => (prev.value > current.value ? prev : current),
        seasonOptions[0]
      );

  const navItems = [
    {
      imgSrc: clubHouseLogo,
      link: `/staff/club/${clubId}`,
      title: 'HOME'
    },
    {
      imgSrc: eventsLogo,
      link: `/staff/club/${clubId}/events`,
      title: 'EVENTS'
    },
    {
      imgSrc: rosterLogo,
      link: `/staff/club/${clubId}/players-roster`,
      title: 'PLAYER ROSTER'
    },
    // Disabled due to https://code.talpor.com/club15/frontend/-/issues/888
    // {
    //   imgSrc: leaderboardLogo,
    //   link: `/staff/club/${clubId}/standings`,
    //   title: 'STANDINGS'
    // },
    {
      imgSrc: financesLogo,
      link: `/staff/club/${clubId}/ledger`,
      title: 'LEDGER'
    },
    {
      imgSrc: settingsLogo,
      link: `/staff/club/${clubId}/club-settings`,
      title: 'CLUB SETTINGS'
    }
  ];

  const logout = async () => {
    await logoutAction();

    navigate('/staff');

    onClose();
  };

  const myAccountItems = [
    {
      link: '/profile/settings',
      title: 'Account Settings'
    },
    // Disabled temporarily
    // {
    //   link: '/b',
    //   title: 'Help'
    // },
    {
      link: '',
      onClick: logout,
      title: 'Logout'
    }
  ];

  return (
    <Drawer
      anchor="left"
      classes={{
        paper: 'admin-sidemenu override staff-scope'
      }}
      className="admin-sidemenu-root override"
      open={open}
      onClose={onClose}
    >
      <div className="menu-header">
        <img src={redWaglLogo} className="logo" alt="Logo" />

        <IconButton className="close" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </div>

      <div className="sidemenu-nav-item go-to-clubhouse withBottomBorder">
        <img src={clubHouseIcon} className="icon" alt={`icon`} />

        <Link className="link" to={'/home'} onClick={onClose}>
          GO TO MY CLUBHOUSE
        </Link>
      </div>

      {isARouteWithPickers(currentRoute) && (
        <div className="sidemenu-pickers">
          {!!clubOptions.length && (
            <AutoComplete
              defaultValue={selectedClub}
              onChange={switchToClub}
              options={clubOptions}
              title="Club Picker"
              className="sidemenu-autocomplete"
            />
          )}

          {!!seasonOptions.length && (
            <AutoComplete
              defaultValue={selectedSeason}
              onChange={switchToSeason}
              options={seasonOptions}
              title="Season"
              className="sidemenu-autocomplete smaller"
            />
          )}
        </div>
      )}

      <nav className="sidemenu-nav">
        {!!clubId &&
          navItems.map(item => (
            <div className="sidemenu-nav-item" key={`${item.title} icon`}>
              <img src={item.imgSrc} className="icon" alt={`${item.title} icon`} />

              <Link className="link" to={item.link} onClick={onClose}>
                {item.title}
              </Link>
            </div>
          ))}
      </nav>

      <p className="section-label">ADMIN TOOLS</p>

      {adminClub ? <SendNotification club={adminClub} /> : <></>}

      {/* Disabled temporarily */}
      {false && (
        <div className="sidemenu-nav-item withBottomBorder admin-tools">
          <img src={addNewClubIcon} className="icon" alt={`icon`} />

          <Link className="link" to={'/staff/club/create/info'} onClick={onClose}>
            START A NEW CLUB
          </Link>
        </div>
      )}

      <p className="section-label">MY ACCOUNT</p>

      {myAccountItems.map(item => (
        <div className="sidemenu-nav-item my-account-item" key={`${item.title} icon`}>
          <Link className="link" to={item.link} onClick={item.onClick ? item.onClick : onClose}>
            {item.title}
          </Link>
        </div>
      ))}
    </Drawer>
  );
};
